import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Plus,
  Search,
  UserPlus,
  Calendar,
  MapPin,
  Briefcase,
  Clock,
  ArrowRight,
  Check,
  User,
  Filter,
  ChevronLeft,
  ChevronRight,
  Building2,
  ArrowLeft,
 
  X,
} from "lucide-react";
import { toast } from "sonner";
import { getSubJobList, getAssignableUsers, } from "../../services/jobsapi";
import { useRbac } from "../../context";

// Define interfaces for type safety
interface User {
  id: string;
  username: string;
  email: string;
}

interface Job {
  id: string;
  parentJobId: string;
  title: string;
  companyName: string;
  brandName: string;
  companyLogoUrl: string;
  location: string;
  workType: string;
  status: string;
  employmentType: string;
  createdAt: string;
  noOfOpenings: number;
  createdBy: User;
  isArchived?: boolean;
}

interface AssignableUser {
  id: string;
  username: string;
  email: string;
  role: string;
}

const ViewSubJobs: React.FC = () => {
  const [subjobs, setSubjobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [jobDetails, setJobDetails] = useState<Job | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("published");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {user}=useRbac();

  // Fetch subjobs
  const fetchSubjobs = async (page = 1) => {
    try {
      setLoading(true);
      setIsLoading(true);
      const params: any = {
        page,
        limit: 12,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await getSubJobList(user.id,user.role, params);
      if (response.status === "success" && response.data?.jobs) {
        // Map jobs to ensure brandName is available
        const mappedJobs = response.data.jobs.map((job: any) => ({
          ...job,
          brandName: job.brandName || job.companyName || '',
        }))
        setSubjobs(mappedJobs);
        setCurrentPage(response.page || page);
        setTotalPages(response.totalPages || 1);
        setTotalResults(response.totalResults || response.data.jobs.length);
      } else {
        console.warn("Unexpected response structure:", response);
        setSubjobs([]);
      }
    } catch (error) {
      console.error("Error fetching sub jobs:", error);
      toast.error("Failed to load sub jobs. Please try again.");
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  // Initial load and search/filter effects
  useEffect(() => {
    fetchSubjobs(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchSubjobs(currentPage);
  }, [currentPage]);

  // Fetch assignable users when dialog opens
  useEffect(() => {
    const fetchAssignableUsers = async () => {
      if (!selectedJob) return;
      try {
        const response = await getAssignableUsers(selectedJob.id);
        if (response.status === "success" && response.data?.availableToAssign) {
          setAssignableUsers(response.data.availableToAssign);
        } else {
          console.warn("Unexpected response structure:", response);
          setAssignableUsers([]);
        }
      } catch (error) {
        console.error("Failed to fetch assignable users:", error);
        toast.error("Failed to load assignable users. Please try again.");
      }
    };

    if (dialogOpen) {
      fetchAssignableUsers();
    }
  }, [dialogOpen, selectedJob]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current?.contains(event.target as Node) === false &&
        inputRef.current?.contains(event.target as Node) === false
      ) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter users based on search term
  useEffect(() => {
    const term = userSearchTerm.toLowerCase();
    const filtered = assignableUsers.filter(
      (user) =>
        user.username.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.role.toLowerCase().includes(term)
    );
    setAssignableUsers(filtered);
  }, [userSearchTerm]);

  // Log job details for debugging
  useEffect(() => {
    console.log("jobDetails:", jobDetails);
  }, [jobDetails]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "published":
        return "bg-green-100 text-green-800 border-green-200";
      case "drafted":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "archived":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const getWorkTypeColor = (workType: string) => {
    switch (workType.toLowerCase()) {
      case "remote":
        return "bg-orange-50 text-orange-700 border-orange-200";
      case "hybrid":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "on-site":
        return "bg-orange-50 text-orange-700 border-orange-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleAssignJob = async () => {
    // if (!selectedJob || selectedUsers.length === 0) {
    //   toast.error("Please select at least one user to assign the job.");
    //   return;
    // }

    // try {
    //   setIsLoading(true);
    //   await assignUsers({ assignToUserIds: selectedUsers }, selectedJob.id);
    //   setDialogOpen(false);
    //   toast.success("Sub job assigned successfully!");
    //   setSelectedUsers([]);
    //   fetchSubjobs(currentPage);
    // } catch (error) {
    //   console.error("Failed to assign sub job:", error);
    //   toast.error("Failed to assign sub job. Please try again.");
    // } finally {
    //   setIsLoading(false);
    // }
  };

  const handleEditJob = (job: Job) => {
    const timestamp = Date.now();
    navigate(`/create-job?mode=edit&jobId=${job.id}&t=${timestamp}`);
  };

  const handleDeleteJob = async (jobId: string) => {
    // if (window.confirm("Are you sure you want to delete this sub job? This action cannot be undone.")) {
    //   try {
    //     // Assuming a deleteSubJob API exists; replace with actual API if different
    //     await deleteJob(jobId);
    //     fetchSubjobs(currentPage);
    //     toast.success("Sub job deleted successfully!");
    //   } catch (error) {
    //     console.error("Failed to delete sub job:", error);
    //     toast.error("Failed to delete sub job. Please try again.");
    //   }
    // }
  };

  const handleArchiveJob = async (jobId: string, currentArchiveStatus: boolean) => {
    // try {
    //   const newArchiveStatus = !currentArchiveStatus;
    //   setSubjobs((prevJobs) =>
    //     prevJobs.map((job) =>
    //       job.id === jobId ? { ...job, isArchived: newArchiveStatus } : job
    //     )
    //   );
    //   await updateJobArchiveStatus(jobId, newArchiveStatus);
    //   const action = newArchiveStatus ? "archived" : "unarchived";
    //   toast.success(`Sub job has been successfully ${action}`);
    //   fetchSubjobs(currentPage);
    // } catch (error) {
    //   console.error("Failed to update archive status:", error);
    //   setSubjobs((prevJobs) =>
    //     prevJobs.map((job) =>
    //       job.id === jobId ? { ...job, isArchived: currentArchiveStatus } : job
    //     )
    //   );
    //   toast.error("Failed to update archive status. Please try again.");
    // }
  };

  const handleViewJobDetails = (jobId: string) => {
    navigate(`/viewSubJob/${jobId}`);
  };



  const handleBackNavigation = () => {
    navigate(-1);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {pageNumbers.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? "default" : "outline"}
            size="sm"
            onClick={() => setCurrentPage(page)}
            disabled={isLoading}
            className={currentPage === page ? "bg-[#EC6E51] text-primary-foreground" : ""}
          >
            {page}
          </Button>
        ))}
        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={isLoading}
            >
              {totalPages}
            </Button>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  const totalOpenings = subjobs.reduce((sum, job) => sum + job.noOfOpenings, 0);

  const filteredSubjobs = subjobs.filter((job) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "published") return job.status.toLowerCase() === "published";
    if (statusFilter === "drafted") return job.status.toLowerCase() === "drafted";
    if (statusFilter === "archived") return job.isArchived;
    return true;
  });

  const getSelectedUserNames = () => {
    return selectedUsers
      .map((id) => {
        const user = assignableUsers.find((u) => u.id === id);
        return user?.username;
      })
      .filter((username): username is string => !!username);
  };

  const handleRemoveUser = (userId: string) => {
    handleUserToggle(userId);
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.focus();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="p-1 sm:p-2"
                onClick={handleBackNavigation}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Sub Jobs Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-500">
                  <span className="hidden sm:inline">Total Openings: {totalOpenings} | </span>
                  <span>Total Sub Jobs: {totalResults}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search sub jobs..."
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64 border-gray-300"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="sm:hidden"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div
            className={`flex flex-col space-y-4 mt-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 ${
              showMobileFilters ? "block" : "hidden sm:flex"
            }`}
          >
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-32 border-gray-300">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="drafted">Draft</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto"
              onClick={() => navigate("/create-subjob")}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Create New Sub Job</span>
              <span className="sm:hidden">Create Sub Job</span>
            </Button>
          </div>
        </div>

        {/* Sub Job Cards Grid */}
        <div className="flex flex-col h-full justify-between overflow-auto p-3 sm:p-6">
          {loading ? (
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {[...Array(12)].map((_, index) => (
                <Card
                  key={`shimmer-${index}`}
                  className="bg-white/90 border-0 shadow-md rounded-2xl overflow-hidden backdrop-blur-sm"
                >
                  <CardContent className="p-6 flex flex-col h-full justify-between">
                    <div className="space-y-4">
                      {/* Job Title and Status Badge */}
                      <div className="flex items-center justify-between">
                        <div className="animate-pulse bg-gray-200 rounded w-full h-6 mb-2"></div>
                        <div className="animate-pulse bg-gray-200 rounded-full w-20 h-6"></div>
                      </div>

                      {/* Location and Employment Type Badges */}
                      <div className="flex flex-wrap gap-2">
                        <div className="animate-pulse bg-gray-200 rounded w-full h-8"></div>
                        <div className="flex gap-2">
                          <div className="animate-pulse bg-gray-200 rounded w-32 h-6"></div>
                          <div className="animate-pulse bg-gray-200 rounded w-20 h-6"></div>
                        </div>
                      </div>

                      {/* Job Stats */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex items-center gap-2">
                            <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                            <div className="animate-pulse bg-gray-200 rounded w-8 h-4"></div>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="animate-pulse bg-gray-200 rounded w-4 h-4"></div>
                            <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <div className="pt-4">
                      <div className="animate-pulse bg-gray-200 rounded-lg w-full h-10"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredSubjobs.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center px-4">
                <p className="text-gray-500 mb-2">No sub jobs found</p>
                <p className="text-sm text-gray-400">
                  {searchTerm ? "Try adjusting your search terms" : "Create your first sub job to get started"}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {filteredSubjobs.map((job) => (
                <Card className="bg-white/90 border-0 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-2 rounded-2xl overflow-hidden backdrop-blur-sm">
      <CardContent className="p-6 flex flex-col h-full justify-between">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 leading-tight hover:text-[#EC6E51] transition-colors duration-200">
              {job.title}
            </h3>
            <Badge
              variant="outline"
              className={`text-xs font-medium px-3 py-1.5 rounded-full ${getStatusBadgeColor(job.status)} transition-colors duration-200`}
            >
              {job.status === 'published' ? 'Published' : job.status === 'drafted' ? 'Draft' : 'Archived'}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            {job.companyLogoUrl ? (
              <img 
                src={job.companyLogoUrl} 
                alt={`${job.brandName || job.companyName} logo`}
                className="h-7 w-7 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
            ) : (
              <Building2 className="h-4 w-4 text-gray-400" />
            )}
            <Building2 className="h-4 w-4 text-gray-400" style={{display: 'none'}} />
            <span className="font-medium">{job.brandName || job.companyName}</span>
          </div>
          <div className="flex flex-wrap gap-2">
             <Badge
                variant="outline"
                className="text-xs font-medium px-2 py-1 rounded-md bg-gray-50 text-gray-700 border-gray-200"
                style={{maxWidth:"100%"}}
              >
              <div> <MapPin className="h-[13px] w-[13px] mr-1" /></div>
                <div className="truncate">
                {job.location}
                </div>
              </Badge>
            {job.employmentType && (
              <Badge
                variant="outline"
                className="text-sm font-medium px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 border-gray-200 flex items-center gap-1 max-w-[220px]"
              >
                <Clock className="h-4 w-4" />
                <div className="truncate">

                {job.employmentType}</div>
              </Badge>
            )}
              <Badge
                variant="outline"
                className={`text-xs font-medium px-2 py-1 rounded-md ${getWorkTypeColor(job.workType)}`}
              >
                <Briefcase className="h-3 w-3 mr-1" />
                {job.workType}
              </Badge>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Openings:</span>
                <span className="text-sm font-bold text-gray-900">{job.noOfOpenings}</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-[#EC6E51]" />
                <span>{formatDate(job.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-4">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-[#EC6E51] hover:text-white hover:bg-[#EC6E51] font-medium px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-[#EC6E51] hover:border-transparent"
            onClick={() => handleViewJobDetails(job.id)}
          >
            View Details
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between sticky bottom-0 bg-white p-4 sticky bottom-[16px] bg-white">
            <div className="text-sm text-gray-500">
              Showing {filteredSubjobs.length} of {totalResults} sub jobs
            </div>
            {totalPages > 1 && renderPagination()}
          </div>
        </div>
      </div>

      {/* Assign Sub Job Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg p-0">
          <div className="bg-white rounded-3xl shadow-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  Assign <span className="text-[#EA6B50]">{selectedJob?.title}</span> Sub Job
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  Select team members to assign this sub job
                </DialogDescription>
              </DialogHeader>
            </div>
            <div className="p-6 space-y-6 relative pt-[10px]">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Team Members</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search team members..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all duration-200"
                  />
                </div>
              </div>
              {isSearchFocused && (
                <div
                  ref={dropdownRef}
                  onMouseDown={handleDropdownClick}
                  className="absolute top-[72px] left-0 mt-2 w-full z-20 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto"
                >
                  {assignableUsers.length > 0 ? (
                    assignableUsers.map((user) => (
                      <div
                        key={user.id}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleUserToggle(user.id);
                        }}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-[#A8A4B6] to-orange-500 rounded-full flex items-center justify-center text-white font-medium text-sm mr-3">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{user.username}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {user.email} • {user.role}
                          </p>
                        </div>
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                            selectedUsers.includes(user.id)
                              ? "bg-orange-500 border-orange-500"
                              : "border-gray-300 hover:border-orange-400"
                          }`}
                        >
                          {selectedUsers.includes(user.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-6 text-center text-gray-500">
                      <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No team members found</p>
                    </div>
                  )}
                </div>
              )}
              {selectedUsers.length > 0 && (
                <div className="bg-orange-50 rounded-2xl p-4 border border-orange-200">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-orange-200 rounded-full flex items-center justify-center mr-3">
                      <UserPlus className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-orange-600">
                        {selectedUsers.length} member{selectedUsers.length !== 1 ? "s" : ""} selected
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {getSelectedUserNames().map((username, index) => {
                          const userId = selectedUsers[index];
                          return (
                            <div
                              key={userId}
                              className="flex items-center bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded-full"
                            >
                              <span className="truncate max-w-[100px]">{username}</span>
                              <button
                                onClick={() => handleRemoveUser(userId)}
                                className="ml-1 hover:text-orange-900 focus:outline-none"
                                style={{ minWidth: "auto", minHeight: "auto" }}
                                aria-label={`Remove ${username}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button
                onClick={() => setDialogOpen(false)}
                className="flex-1 px-4 py-3 text-gray-700 font-medium rounded-2xl border border-gray-200 hover:bg-gray-50 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignJob}
                disabled={selectedUsers.length === 0 || isLoading}
                className="flex-1 px-4 py-3 bg-orange-600 text-white font-medium rounded-2xl hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl disabled:shadow-none"
              >
                {isLoading ? "Assigning..." : "Assign Sub Job"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewSubJobs;